/* ==========================================================================
   <farmahanem-cart-gifts> — hediye ürünü sepete otomatik ekler / çıkarır
   --------------------------------------------------------------------------
   Shopify'ın "X alana Y" otomatik indirimi hediyeyi sepete eklemez; bu bileşen
   ekler. Kurallar ve sepetin o anki durumu sections/farmahanem-cart-gifts.liquid
   tarafından JSON olarak basılır; sepet her değiştiğinde bölüm yeniden istenir
   ve gerekirse sepet düzeltilir. Tema sepet değişimini tek bir olayla bildirmez:
   adet değişimi `on:cart:change`, ürün sayfasından ekleme `on:cart:add`, ürün
   kartından ekleme `farmahanem:cart:item-added` yayar; hepsi dinlenir. Bunların
   dışında kalan yollar için sepet arayüzünün yeniden çizilmesi
   (`on:cart:after-merge`) de tetikleyicidir.
   - Eşik geçildi, hediye sepette yok        → ekle (_fh_hediye özelliğiyle)
   - Eşiğin altına inildi                    → bizim eklediğimiz satırı çıkar
   - Eklendi ama Shopify indirim uygulamadı  → geri çıkar (müşteriye tam fiyat
     yazılmasın); sepet büyüyene kadar yeniden deneme
   - Müşteri hediyeyi kendi sildi            → bu sepette bir daha ekleme
   "Tek hediye" açıkken sepet tutarı kurallarından yalnız en yüksek eşiğin
   hediyesi verilir; "ürün alana" kuralları (koşul ürünü sepette) bundan bağımsızdır.
   Müşterinin kendi eklediği (özelliksiz) satırlara dokunulmaz.
   ========================================================================== */

const GIFT_PROPERTY = '_fh_hediye';
const FLAGS_KEY = 'fh-cart-gifts';
const MAX_PASSES = 4;
const CART_EVENTS = ['on:cart:change', 'on:cart:add', 'farmahanem:cart:item-added'];
const DEBOUNCE_MS = 100;
// Kendi eşitlememizin ardından gelen yeniden çizim yeni bir değişiklik değildir.
const RENDER_IGNORE_MS = 2000;

class FarmahanemCartGifts extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.exclusive = this.dataset.exclusive !== 'false';
    this.root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    this.busy = false;
    this.pending = false;
    this.ownEvent = false;
    this.lastSyncEnd = 0;

    this.onCartEvent = () => {
      if (!this.ownEvent) this.schedule();
    };
    this.onCartRendered = () => {
      if (this.busy || Date.now() - this.lastSyncEnd < RENDER_IGNORE_MS) return;
      this.schedule();
    };
    CART_EVENTS.forEach((name) => document.addEventListener(name, this.onCartEvent));
    document.addEventListener('on:cart:after-merge', this.onCartRendered);

    // Sayfa açılışı: basılı durum bir işlem gerektiriyorsa taze durumla eşitle.
    const initial = this.parseState(this);
    if (initial && this.plan(initial, { dryRun: true }).length) this.sync();
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    CART_EVENTS.forEach((name) => document.removeEventListener(name, this.onCartEvent));
    document.removeEventListener('on:cart:after-merge', this.onCartRendered);
  }

  /** Aynı anda gelen olayları (ör. ekleme + yeniden çizim) tek eşitlemede toplar. */
  schedule() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.sync(), DEBOUNCE_MS);
  }

  parseState(scope) {
    try {
      return JSON.parse(scope.querySelector('[data-farmahanem-cart-gifts]').textContent);
    } catch (error) {
      return null;
    }
  }

  async fetchState() {
    const response = await fetch(`${this.root}?section_id=${encodeURIComponent(this.sectionId)}`);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    return this.parseState(doc);
  }

  /** Kural başına { added, dismissed, failedAt } — oturum boyunca, sepet boşalınca sıfırlanır. */
  loadFlags(state) {
    if (!state.itemCount) return {};
    try {
      return JSON.parse(sessionStorage.getItem(FLAGS_KEY)) || {};
    } catch (error) {
      return {};
    }
  }

  saveFlags(flags) {
    try {
      sessionStorage.setItem(FLAGS_KEY, JSON.stringify(flags));
    } catch (error) {
      /* storage kapalı — bayraklar bu sayfayla sınırlı kalır */
    }
  }

  /** Durumdan yapılacak işlemleri çıkarır: [{ type: 'add' | 'remove', rule }]. */
  plan(state, { dryRun = false } = {}) {
    const flags = this.loadFlags(state);
    const rules = [...(state.rules || [])].sort((a, b) => b.threshold - a.threshold);

    rules.forEach((rule) => {
      const flag = flags[rule.id] || (flags[rule.id] = {});
      // Biz eklemiştik, eşik hâlâ sağlanıyor ama satır yok → müşteri sildi.
      if (flag.added && rule.gift.qty === 0) {
        if (rule.eligible >= rule.threshold) flag.dismissed = true;
        flag.added = false;
      }
      // Sepet büyüdüyse başarısız denemeyi unut.
      if (flag.failedAt != null && rule.eligible > flag.failedAt) flag.failedAt = null;
    });

    const winners = new Set();
    const qualifies = (rule) =>
      rule.gift.available && rule.eligible >= rule.threshold && flags[rule.id].failedAt == null;
    // "Ürün alana" kuralları kademe değildir; tek hediye ayarından bağımsız verilir.
    rules
      .filter((rule) => rule.type === 'product')
      .forEach((rule) => {
        if (qualifies(rule) && !flags[rule.id].dismissed) winners.add(rule.id);
      });
    for (const rule of rules.filter((r) => r.type !== 'product')) {
      if (!qualifies(rule)) continue;
      if (!flags[rule.id].dismissed) winners.add(rule.id);
      // Tek hediye: en yüksek eşik belirleyici; müşteri onu sildiyse alt kademe verilmez.
      if (this.exclusive) break;
    }

    const actions = [];
    rules.forEach((rule) => {
      const flag = flags[rule.id];
      const want = winners.has(rule.id);
      const ours = rule.gift.autoKeys.length > 0;
      if (want && rule.gift.qty === 0) {
        actions.push({ type: 'add', rule });
      } else if (want && ours && !rule.gift.discounted) {
        actions.push({ type: 'remove', rule });
        flag.failedAt = rule.eligible;
      } else if (!want && ours) {
        actions.push({ type: 'remove', rule });
      }
    });

    if (!dryRun) {
      this.flags = flags;
      this.saveFlags(flags);
    }
    return actions;
  }

  async post(path, body) {
    const response = await fetch(`${this.root}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    return response.ok;
  }

  async apply({ type, rule }) {
    const flag = this.flags[rule.id];
    if (type === 'add') {
      const ok = await this.post('cart/add.js', {
        items: [{ id: rule.gift.variant, quantity: 1, properties: { [GIFT_PROPERTY]: 'otomatik' } }]
      });
      if (ok) {
        flag.added = true;
      } else {
        // Eklenemedi (stok vb.) — sepet büyüyene kadar yeniden deneme.
        flag.failedAt = rule.eligible;
      }
    } else {
      for (const key of rule.gift.autoKeys) {
        await this.post('cart/change.js', { id: key, quantity: 0 });
      }
      flag.added = false;
    }
    this.saveFlags(this.flags);
  }

  async sync() {
    if (this.busy) {
      this.pending = true;
      return;
    }
    this.busy = true;
    let mutated = false;
    try {
      // Her ekleme/çıkarma sepeti değiştirir; durum sakinleşene kadar yeniden değerlendir.
      for (let pass = 0; pass < MAX_PASSES; pass += 1) {
        const state = await this.fetchState();
        if (!state) break;
        const actions = this.plan(state);
        if (!actions.length) break;
        for (const action of actions) {
          await this.apply(action);
        }
        mutated = true;
      }
    } catch (error) {
      console.error('[farmahanem-cart-gifts]', error);
    } finally {
      this.busy = false;
      this.lastSyncEnd = Date.now();
      if (mutated) {
        // Sepet çekmecesi, sepet sayfası ve başlıktaki sayaç bu olayla yenilenir.
        this.ownEvent = true;
        document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
        this.ownEvent = false;
      }
      if (this.pending) {
        this.pending = false;
        this.sync();
      }
    }
  }
}

if (!customElements.get('farmahanem-cart-gifts')) {
  customElements.define('farmahanem-cart-gifts', FarmahanemCartGifts);
}
